import type { ReactNode } from 'react'
import { cn } from '@/shared/lib/cn'
import { Container } from './Container'
import { Backdrop } from './Backdrop'
import { Eyebrow } from './Eyebrow'

type SectionProps = {
  children: ReactNode
  /** Muted background for alternating bands. */
  tone?: 'default' | 'surface' | 'brand'
  /** Vertical rhythm. Sections are never spaced with one-off margins. */
  size?: 'sm' | 'md' | 'lg'
  /**
   * Optional decorative layer. Rendered outside the Container so it spans the
   * full viewport width rather than being clipped to the content measure.
   */
  backdrop?: 'grid' | 'dots' | 'grid-dark'
  id?: string
  className?: string
  as?: 'section' | 'div'
}

const tones = {
  default: 'bg-white',
  surface: 'bg-surface',
  brand: 'bg-brand-950 text-white',
}

const sizes = {
  sm: 'py-12 sm:py-16',
  md: 'py-16 sm:py-24',
  lg: 'py-20 sm:py-28 lg:py-32',
}

export function Section({
  children,
  tone = 'default',
  size = 'md',
  backdrop,
  id,
  className,
  as: Tag = 'section',
}: SectionProps) {
  return (
    <Tag
      id={id}
      className={cn(tones[tone], sizes[size], backdrop && 'relative isolate overflow-hidden', className)}
    >
      {backdrop && <Backdrop variant={backdrop} />}
      <Container className={cn(backdrop && 'relative')}>{children}</Container>
    </Tag>
  )
}

/** Standard section heading block: eyebrow, title, optional lead paragraph. */
export function SectionHeader({
  eyebrow,
  title,
  lead,
  align = 'left',
  tone = 'default',
  as: Heading = 'h2',
}: {
  eyebrow?: string
  title: string
  lead?: string
  align?: 'left' | 'center'
  /** Use `inverse` on dark brand bands. */
  tone?: 'default' | 'inverse'
  as?: 'h1' | 'h2'
}) {
  const inverse = tone === 'inverse'

  return (
    <div className={cn('max-w-3xl', align === 'center' && 'mx-auto text-center')}>
      {eyebrow && (
        <Eyebrow
          tone={inverse ? 'inverse' : 'default'}
          className={cn('mb-5', align === 'center' && 'justify-center')}
        >
          {eyebrow}
        </Eyebrow>
      )}
      <Heading
        className={cn(
          'font-semibold text-balance',
          inverse ? 'text-white' : 'text-ink',
          Heading === 'h1'
            ? 'text-4xl sm:text-5xl lg:text-[3.5rem] lg:leading-[1.05]'
            : 'text-3xl sm:text-4xl',
        )}
      >
        {title}
      </Heading>
      {lead && (
        <p
          className={cn(
            'mt-5 text-lg leading-relaxed text-pretty',
            inverse ? 'text-brand-200' : 'text-muted',
          )}
        >
          {lead}
        </p>
      )}
    </div>
  )
}
